import { Component, DestroyRef, inject, OnInit, PLATFORM_ID, computed, signal } from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { PhoneInputComponent } from '../../../shared-with-client/phone-input/phone-input.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StoresService } from '../../../core/services/stores.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TeyaService, ITeyaPaymentStatus } from '../../../core/services/teya.service';
import { InvoicingService } from '../../../core/services/invoicing.service';
import { MerchantCurrencyPipe } from '../../pipes/merchant-currency.pipe';
import { from, Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ChangeData } from 'untold-ngx-intl-tel-input';

declare const Stripe: stripe.StripeStatic;

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 10 * 60 * 1000;
const FINAL_PAYMENT_STATUSES = ['SUCCESSFUL', 'FAILED', 'CANCELLED'];
const SUCCESSFUL_PAYMENT_STATUS = 'SUCCESSFUL';

type ChargeMethod = 'terminal' | 'bukpay' | 'mbway_request';
type Phase = 'choose' | 'waiting' | 'link-sent' | 'mbway-sent' | 'success' | 'failed';

interface MethodOption {
    id: ChargeMethod;
    labelKey: string;
    hintKey: string;
    icon?: string;
    logoSrc?: string;
    activateLabelKey: string;
    activateHintKey: string;
    activatePath: string;
}

@Component({
    selector: 'app-simple-payment-modal',
    imports: [
        LoaderComponent,
        IconComponent,
        ButtonComponent,
        TextInputComponent,
        PhoneInputComponent,
        ReactiveFormsModule,
        TranslatePipe,
        MerchantCurrencyPipe,
        RouterLink,
        NgClass,
    ],
    templateUrl: './simple-payment-modal.component.html',
    styleUrl: './simple-payment-modal.component.scss',
})
export class SimplePaymentModalComponent implements OnInit {
    private storesService = inject(StoresService);
    private formBuilder = inject(FormBuilder);
    public currentStore = toSignal(this.storesService.getActiveStore());
    public dialogRef = inject(DialogRef<any>);
    private teyaService = inject(TeyaService);
    private invoicingService = inject(InvoicingService);
    private translateService = inject(TranslateService);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private platformId = inject(PLATFORM_ID);

    public data = inject(DIALOG_DATA) as {
        bookingId?: number;
        amount?: number;
        clientName?: string;
        clientEmail?: string;
        clientPhone?: string;
    };

    public submitAttempted = signal(false);
    public form!: FormGroup;

    public loading = signal(false);
    public saving = signal(false);
    public phase = signal<Phase>('choose');
    public paymentMethod = signal<ChargeMethod>('mbway_request');
    public errorMessage = signal<string | null>(null);
    public cancellingTeyaPayment = signal(false);
    public sentContact = signal<string>('');
    /** Once the user picks a method, store refresh must not reset it (race with getStore). */
    private userSelectedPaymentMethod = false;

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());
    public teyaConnectionStatus = toSignal(
        this.teyaService.checkOrFetchConnectionStatus(this.currentStore()?.id),
    );

    public hasTerminal = computed(() => this.teyaConnectionStatus()?.active === true);
    /** Same EuPago / Stripe rules as checkout Buk Pay (without requiring sales module). */
    public hasBukPay = computed(() => this.storesService.isBukPayBookingLinkActive(this.currentStore()));
    public hasMbWay = computed(() => this.storesService.isStripeMbWayActive(this.currentStore()));
    public hasAnyActiveMethod = computed(() => this.hasTerminal() || this.hasBukPay() || this.hasMbWay());
    public isSolo = computed(() => !this.data.bookingId);
    /** Solo Teya always needs an amount; with booking, only when a charge method is active. */
    public showAmountInput = computed(() => this.isSolo() || this.hasAnyActiveMethod());

    public isSalesModuleActive = computed(() => !!this.currentStore()?.salesModuleActive);
    /** Upsell sales / invoicing whenever those modules are off (not only when no charge method works). */
    public showModuleUpsells = computed(
        () => !this.isSolo() && (!this.isSalesModuleActive() || !this.isInvoicingActive()),
    );

    /** Display / default priority: MB Way → Buk Pay → Teya. */
    public methods: MethodOption[] = [
        {
            id: 'mbway_request',
            labelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.sendMbWayRequest',
            hintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.mbWayRequestHint',
            logoSrc: 'assets/mbway-mark.png',
            activateLabelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateMbWay',
            activateHintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateMbWayHint',
            activatePath: 'extensions/payments',
        },
        {
            id: 'bukpay',
            labelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.sendPaymentLink',
            hintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.paymentLinkHint',
            logoSrc: 'assets/buk-icon.png',
            activateLabelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateBukPay',
            activateHintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateBukPayHint',
            activatePath: 'extensions/payments',
        },
        {
            id: 'terminal',
            labelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.sendToTerminal',
            hintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.teyaTerminal',
            logoSrc: 'assets/teya-logo.png',
            activateLabelKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateTerminal',
            activateHintKey: 'SHARED.SIMPLE_PAYMENT_MODAL.activateTerminalHint',
            activatePath: 'extensions/teya',
        },
    ];

    private stopPolling$ = new Subject<void>();
    private activeSaleId: number | null = null;
    private activePaymentRequestId: string | null = null;
    private stripe: stripe.Stripe | null = null;

    ngOnInit(): void {
        const minAmount = this.data.bookingId ? 0.5 : 0.01;
        this.form = this.formBuilder.group({
            amount: [
                this.data.amount != null ? Math.max(this.data.amount / 100, 0) : null,
                [Validators.required, Validators.min(minAmount)],
            ],
            email: [this.data.clientEmail || '', [Validators.email]],
            phoneNumber: [this.data.clientPhone || ''],
        });

        if (isPlatformBrowser(this.platformId) && typeof Stripe === 'function') {
            this.stripe = Stripe(environment.stripe.publishableKey);
        }

        // Refresh active store so payment flags reflect extensions toggles without a full page reload.
        const storeId = this.currentStore()?.id;
        if (storeId) {
            this.storesService.getStore(this.currentStore()?.slug).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (store) => {
                    this.storesService.patchMyStore(store);
                    this.applyDefaultPaymentMethodIfNeeded();
                },
                error: () => {
                    this.applyDefaultPaymentMethodIfNeeded();
                },
            });
        } else {
            this.applyDefaultPaymentMethodIfNeeded();
        }
    }

    private applyDefaultPaymentMethodIfNeeded(): void {
        if (this.userSelectedPaymentMethod) {
            return;
        }
        this.paymentMethod.set(this.defaultPaymentMethod());
        this.syncContactValidators(this.paymentMethod());
    }

    visibleMethods(): MethodOption[] {
        if (this.isSolo()) {
            return this.methods.filter((m) => m.id === 'terminal');
        }

        return this.methods;
    }

    isMethodActive(id: ChargeMethod): boolean {
        if (id === 'terminal') {
            return this.hasTerminal();
        }
        if (id === 'bukpay') {
            return this.hasBukPay();
        }
        return this.hasMbWay();
    }

    selectedMethodOption(): MethodOption | undefined {
        return this.methods.find((m) => m.id === this.paymentMethod());
    }

    needsActivation(): boolean {
        return !this.isMethodActive(this.paymentMethod());
    }

    selectPaymentMethod(id: ChargeMethod): void {
        this.userSelectedPaymentMethod = true;
        this.paymentMethod.set(id);
        this.syncContactValidators(id);
    }

    private defaultPaymentMethod(): ChargeMethod {
        if (this.isSolo()) {
            return 'terminal';
        }
        if (this.hasMbWay()) {
            return 'mbway_request';
        }
        if (this.hasBukPay()) {
            return 'bukpay';
        }
        if (this.hasTerminal()) {
            return 'terminal';
        }
        return 'mbway_request';
    }

    private syncContactValidators(paymentMethod: ChargeMethod): void {
        const email = this.form.get('email');
        const phone = this.form.get('phoneNumber');

        if (paymentMethod === 'bukpay') {
            email?.setValidators([Validators.required, Validators.email]);
            phone?.clearValidators();
        } else if (paymentMethod === 'mbway_request') {
            phone?.setValidators([Validators.required]);
            email?.setValidators([Validators.email]);
        } else {
            email?.clearValidators();
            phone?.clearValidators();
            email?.setValidators([Validators.email]);
        }

        email?.updateValueAndValidity({ emitEvent: false });
        phone?.updateValueAndValidity({ emitEvent: false });
    }

    private parsePhoneNumber(value: ChangeData | string | null): string {
        if (!value) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        return value.e164Number || value.internationalNumber || value.number || '';
    }

    private amountCents(): number {
        return Math.round(Number(this.form.value.amount) * 100);
    }

    submit(): void {
        if (this.saving()) {
            return;
        }

        this.submitAttempted.set(true);

        if (this.needsActivation()) {
            this.goActivate();
            return;
        }

        if (this.form.invalid) {
            return;
        }

        const paymentMethod = this.paymentMethod();
        if (paymentMethod === 'terminal') {
            this.sendToTerminal();
        } else if (paymentMethod === 'bukpay') {
            this.sendPaymentLink();
        } else {
            this.sendMbWayRequest();
        }
    }

    goActivate(): void {
        const option = this.selectedMethodOption();
        const slug = this.currentStore()?.slug;
        if (!option || !slug) {
            return;
        }
        this.dialogRef.close();
        this.router.navigateByUrl(`/${slug}/dashboard/${option.activatePath}`);
    }

    sendToTerminal(): void {
        this.errorMessage.set(null);
        this.saving.set(true);
        this.phase.set('waiting');
        const amountCents = this.amountCents();
        const storeId = this.currentStore()?.id;

        const request$ = this.data.bookingId
            ? this.teyaService.initiateBookingPayment(storeId, this.data.bookingId, amountCents)
            : this.teyaService.initiateSoloPayment(storeId, amountCents);

        request$.subscribe({
            next: (result) => {
                this.activeSaleId = result.saleId;
                this.activePaymentRequestId = result.paymentRequestId;
                this.pollPaymentStatus(result.saleId, result.paymentRequestId);
            },
            error: (error) => {
                this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
            },
        });
    }

    private sendPaymentLink(): void {
        const storeId = this.currentStore()?.id;
        const bookingId = this.data.bookingId;
        if (!storeId || !bookingId) {
            return;
        }

        this.saving.set(true);
        const email = (this.form.value.email || '').trim();
        this.storesService
            .createBookingPaymentLink(storeId, bookingId, {
                amountCents: this.amountCents(),
                sendEmail: true,
                email,
            })
            .subscribe({
                next: () => {
                    this.saving.set(false);
                    this.sentContact.set(email);
                    this.phase.set('link-sent');
                },
                error: (error) => {
                    this.saving.set(false);
                    this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
                },
            });
    }

    private sendMbWayRequest(): void {
        const storeId = this.currentStore()?.id;
        const bookingId = this.data.bookingId;
        if (!storeId || !bookingId) {
            return;
        }

        this.saving.set(true);
        this.errorMessage.set(null);
        const phoneNumber = this.parsePhoneNumber(this.form.value.phoneNumber);

        this.storesService.chargeBookingMbWay(storeId, bookingId, this.amountCents()).subscribe({
            next: async (result) => {
                try {
                    const paymentIntent = await this.confirmMbWayPayment(result.clientSecret, phoneNumber);
                    this.sentContact.set(phoneNumber);
                    this.handleMbWayPaymentIntent(paymentIntent, result.clientSecret);
                } catch (error: any) {
                    this.markPaymentFailed(
                        error?.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                    );
                }
            },
            error: (error) => {
                this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
            },
        });
    }

    private async confirmMbWayPayment(
        clientSecret: string,
        phoneNumber: string,
    ): Promise<stripe.paymentIntents.PaymentIntent | undefined> {
        if (!isPlatformBrowser(this.platformId)) {
            throw new Error(this.translateService.instant('GENERAL.generalErrorMsg'));
        }

        if (!this.stripe) {
            if (typeof Stripe !== 'function') {
                throw new Error(this.translateService.instant('GENERAL.generalErrorMsg'));
            }
            this.stripe = Stripe(environment.stripe.publishableKey);
        }

        // handleActions: false returns as soon as Stripe sends the MB Way push
        // (status requires_action). We poll until the customer approves/declines.
        const result = await this.stripe.confirmMbWayPayment(
            clientSecret,
            {
                payment_method: {
                    billing_details: {
                        phone: phoneNumber,
                        email: this.form.value.email || undefined,
                    },
                },
            },
            { handleActions: false },
        );

        if (result.error) {
            throw new Error(result.error.message);
        }

        return result.paymentIntent;
    }

    private handleMbWayPaymentIntent(
        paymentIntent: stripe.paymentIntents.PaymentIntent | undefined,
        clientSecret: string,
    ): void {
        const status = paymentIntent?.status;

        if (status === 'succeeded') {
            this.saving.set(false);
            this.phase.set('success');
            return;
        }

        if (status === 'requires_action' || status === 'processing') {
            this.saving.set(false);
            this.phase.set('mbway-sent');
            this.pollMbWayPaymentIntent(clientSecret);
            return;
        }

        this.markPaymentFailed(
            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
        );
    }

    private pollMbWayPaymentIntent(clientSecret: string): void {
        let reachedFinalStatus = false;

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => {
                    if (!this.stripe) {
                        return from(Promise.reject(new Error(this.translateService.instant('GENERAL.generalErrorMsg'))));
                    }

                    return from(this.stripe.retrievePaymentIntent(clientSecret));
                }),
                takeWhile((result) => {
                    const status = result.paymentIntent?.status;
                    return status === 'requires_action' || status === 'processing';
                }, true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (result) => {
                    if (result.error) {
                        reachedFinalStatus = true;
                        this.markPaymentFailed(
                            result.error.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                        );
                        return;
                    }

                    const status = result.paymentIntent?.status;
                    if (status === 'requires_action' || status === 'processing') {
                        return;
                    }

                    reachedFinalStatus = true;
                    if (status === 'succeeded') {
                        this.phase.set('success');
                    } else {
                        this.markPaymentFailed(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
                        );
                    }
                },
                error: (error) => {
                    this.markPaymentFailed(
                        error?.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                    );
                },
                complete: () => {
                    if (!reachedFinalStatus && this.phase() === 'mbway-sent') {
                        this.markPaymentFailed(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'),
                        );
                    }
                },
            });
    }

    getPaymentStatus(saleId: number, paymentRequestId: string) {
        return this.teyaService.getPaymentStatus(saleId, paymentRequestId);
    }

    private pollPaymentStatus(saleId: number, paymentRequestId: string): void {
        let reachedFinalStatus = false;
        this.errorMessage.set(null);

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.getPaymentStatus(saleId, paymentRequestId)),
                takeWhile((status) => !this.isFinalPaymentStatus(status), true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (status: ITeyaPaymentStatus) => {
                    if (this.isFinalPaymentStatus(status)) {
                        reachedFinalStatus = true;
                        this.saving.set(false);
                        this.activePaymentRequestId = null;
                        if (this.isSuccessfulPaymentStatus(status)) {
                            this.activeSaleId = null;
                            this.phase.set('success');
                        } else {
                            this.markPaymentFailed(
                                this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
                            );
                        }
                    }
                },
                error: (error) => {
                    this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
                },
                complete: () => {
                    if (!reachedFinalStatus && this.saving()) {
                        this.markPaymentFailed(this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'));
                    }
                },
            });
    }

    private isFinalPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return FINAL_PAYMENT_STATUSES.includes(status.status);
    }

    private isSuccessfulPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return status.status === SUCCESSFUL_PAYMENT_STATUS;
    }

    cancelWaitingPayment() {
        const saleId = this.activeSaleId;
        const paymentRequestId = this.activePaymentRequestId;

        if (!saleId || !paymentRequestId || this.cancellingTeyaPayment()) {
            this.finishLocalCancelAsFailed();
            return;
        }

        this.cancellingTeyaPayment.set(true);
        this.teyaService.cancelPayment(saleId, paymentRequestId).subscribe({
            next: (status) => {
                this.cancellingTeyaPayment.set(false);
                this.handleCancelOutcome(status);
            },
            error: (error) => {
                this.cancellingTeyaPayment.set(false);

                if (this.isAlreadyFinishedCancelError(error)) {
                    this.reconcileAfterCancelConflict(saleId, paymentRequestId);
                    return;
                }

                this.finishLocalCancelAsFailed(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private handleCancelOutcome(status: ITeyaPaymentStatus) {
        if (this.isSuccessfulPaymentStatus(status)) {
            this.finishLocalCancelAsPaid();
            return;
        }

        this.finishLocalCancelAsFailed();
    }

    private reconcileAfterCancelConflict(saleId: number, paymentRequestId: string) {
        this.teyaService.getPaymentStatus(saleId, paymentRequestId, true).subscribe({
            next: (status) => {
                this.handleCancelOutcome(status);
            },
            error: (error) => {
                this.finishLocalCancelAsFailed(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private isAlreadyFinishedCancelError(error: any): boolean {
        if (error?.errData?.code === 'TEYA_PAYMENT_ALREADY_FINISHED') {
            return true;
        }

        const message = String(error?.errMsg || error?.message || '').toLowerCase();

        return message.includes('already finished');
    }

    private finishLocalCancelAsPaid() {
        this.stopPolling$.next();
        this.saving.set(false);
        this.errorMessage.set(null);
        this.phase.set('success');
        this.activeSaleId = null;
        this.activePaymentRequestId = null;
    }

    private finishLocalCancelAsFailed(message?: string) {
        this.stopPolling$.next();
        this.activePaymentRequestId = null;
        this.markPaymentFailed(
            message || this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
        );
    }

    private markPaymentFailed(message: string) {
        this.saving.set(false);
        this.phase.set('failed');
        this.errorMessage.set(message);
        this.discardEphemeralSaleIfNeeded();
    }

    /**
     * Quick-pay Teya creates an ephemeral pending sale; remove it on failure
     * so it never appears in lists/reports.
     */
    private discardEphemeralSaleIfNeeded(): void {
        const saleId = this.activeSaleId;
        this.activeSaleId = null;

        if (!saleId) {
            return;
        }

        this.invoicingService.discardEphemeralCheckoutSale(saleId).subscribe({
            error: () => {
                // Best-effort: sale may already be gone or no longer ephemeral.
            },
        });
    }

    cancelFailure(): void {
        this.phase.set('choose');
        this.errorMessage.set(null);
    }

    retry(): void {
        this.phase.set('choose');
        this.errorMessage.set(null);
        this.submit();
    }

    cancelMbWayWaiting(): void {
        this.stopPolling$.next();
        this.dialogRef.close();
    }

    close(reason?: string) {
        // mbway-sent means the push was sent but the customer has not paid yet.
        const successPhases: Phase[] = ['success', 'link-sent'];
        this.dialogRef.close(reason ?? (successPhases.includes(this.phase()) ? 'success' : undefined));
    }

    extensionsBase(): string {
        return `${this.currentStore()?.slug}/dashboard`;
    }
}
